import { API_ENDPOINTS } from '../constants/api';
import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';

class CreditService {
  /**
   * Fetch wallet balance (requires Authorization header)
   * @returns {Promise<Object>} Wallet balance response
   */
  async getWalletBalance(subscriberId) {
    try {
      const requestConfig =
        subscriberId == null || subscriberId === ''
          ? undefined
          : { params: { subscriberId: String(subscriberId) } };
      const response = await apiClient.get(API_ENDPOINTS.WALLET_BALANCE, requestConfig);
      return ResponseHandler.handleSuccess(response, 'Wallet balance fetched successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch wallet balance');
    }
  }

  /**
   * Fetch purchase credits list
   * @returns {Promise<Object>} Credit list response
   */
  async getPurchaseCreditsList() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.PURCHASE_CREDITS);
      return ResponseHandler.handleSuccess(response, 'Credits list fetched successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch credits list');
    }
  }

  /**
   * Initiate credit purchase (CCBill flow – returns paymentForm or one-click success)
   * @param {string} creditPackId - Credit pack ID to purchase
   * @param {Object} purchasePayload - { amount, baseAmount, extraCharge, returnUrl, vatAmount, vatPercentage, paymentTokenId? }
   * @returns {Promise<Object>} Purchase result with paymentForm, paymentGateway, or paymentOneClick
   */
  async purchaseCredits(creditPackId, purchasePayload) {
    try {
      const body = {
        creditPackId,
        amount: purchasePayload.amount,
        baseAmount: purchasePayload.baseAmount ?? purchasePayload.amount,
        extraCharge: purchasePayload.extraCharge ?? 0,
        returnUrl: purchasePayload.returnUrl,
        vatAmount: purchasePayload.vatAmount ?? 0,
        vatPercentage: purchasePayload.vatPercentage ?? 0,
      };
      if (purchasePayload.paymentTokenId != null && purchasePayload.paymentTokenId !== '') {
        body.paymentTokenId = purchasePayload.paymentTokenId;
      }
      const response = await apiClient.post(API_ENDPOINTS.PURCHASE_CREDITS_BUY, body);
      return ResponseHandler.handleSuccess(response, 'Credit purchase initiated successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to initiate credit purchase');
    }
  }

  /**
   * Cancel a credit purchase (pending transaction)
   * @param {string} transactionId - Transaction/purchase ID to cancel
   * @param {boolean} isManualRemove - Whether user cancelled (true) or closed (false)
   * @returns {Promise<Object>} Cancel result
   */
  async cancelCreditPurchase(transactionId, isManualRemove = false) {
    try {
      const response = await apiClient.delete(
        `${process.env.REACT_APP_API}/${API_ENDPOINTS.CANCEL_CREDITS_PURCHASE}/${transactionId}/${isManualRemove}`
      );
      return ResponseHandler.handleSuccess(response, 'Credit purchase cancelled successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to cancel credit purchase');
    }
  }

  /**
   * Deduct subscriber wallet credits after a billable call (subscriber only).
   * @param {{ creatorId: string, credit: string, callInMinutes: number, callInSec: number, callMediaType: string }} body
   */
  async deductCallCredit(body) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.DEDUCT_CALL_CREDIT, body);
      return ResponseHandler.handleSuccess(response, 'Call credits deducted successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to deduct call credits');
    }
  }
}

export default new CreditService();
